/**
 * Grouping parsed rows into manufactures, resolving products against the catalog.
 *
 * - Rows without `№ производства` are one manufacture each, using the product's recipe.
 * - Rows with a № are grouped per FILE + №; the file's ingredient lines replace the recipe.
 *   All rows of a group must agree on date, product, quantity and batch (and on whether they
 *   carry an ingredient), otherwise every row of the group is a GROUP_MISMATCH and no draft
 *   is produced. Nothing is ever merged by "same date + product" — that would silently fuse
 *   two real runs.
 *
 * A recipe-driven row is refused (INVALID_RECIPE) when any recipe line lacks a usable quantity.
 *
 * Pure: the caller passes the ProductIndex it already holds.
 */

/* eslint-disable @typescript-eslint/no-explicit-any -- product docs are untyped across Firestore/Supabase read modes (same ruling as utils/salesImport/resolve.ts) */

import { type ManufactureProductIndex, resolveProductStrict } from './resolve'
import {
  type GroupManufacturesResult,
  type ManufactureDraft,
  type ManufactureImportError,
  type ManufactureImportErrorCode,
  type ParsedManufactureRow,
} from './types'

const KEY_SEPARATOR = '\u0000'

const rowKey = (row: ParsedManufactureRow): string =>
  `${row.ref.fileName}${KEY_SEPARATOR}row${KEY_SEPARATOR}${row.ref.rowNumber}`

const numberKey = (row: ParsedManufactureRow): string =>
  `${row.ref.fileName}${KEY_SEPARATOR}no${KEY_SEPARATOR}${row.manufactureNo}`

const sameDate = (a: ParsedManufactureRow['date'], b: ParsedManufactureRow['date']): boolean =>
  a.year === b.year &&
  a.month === b.month &&
  a.day === b.day &&
  a.hasTime === b.hasTime &&
  a.hour === b.hour &&
  a.minute === b.minute &&
  a.second === b.second

const isCompositeProduct = (product: any): boolean => product?.isComposite === true

/**
 * A recipe line's per-unit quantity as a number, or `null` when it is not usable: missing, not a
 * number, not finite, or not above zero. A numeric string is accepted — recipes saved before
 * ATL-633 hold them, and every consumer reads them as numbers (`buildManufactureInfo` writes the
 * number). An unusable one must be refused before writing: `saveNewManufacture` commits
 * Firestore first, and the dual-write sends `Number(quantity)` → NaN → null, which the backend's
 * `@IsNumber()` rejects with no rollback.
 */
export const recipeLineQuantity = (value: unknown): number | null => {
  const quantity =
    typeof value === 'number' ? value : typeof value === 'string' && value.trim() !== '' ? Number(value) : Number.NaN
  return Number.isFinite(quantity) && quantity > 0 ? quantity : null
}

export const groupManufactures = (
  rows: ParsedManufactureRow[],
  index: ManufactureProductIndex
): GroupManufacturesResult => {
  const drafts: ManufactureDraft[] = []
  const errors: ManufactureImportError[] = []

  // A № that carries ingredient lines in a file is a multi-row manufacture. A row WITHOUT an
  // ingredient is otherwise always its own manufacture (spec: without ingredient columns every
  // row is one manufacture), whatever its № — merging two such rows would silently undercount
  // production. A no-ingredient row sharing a № with ingredient rows stays in that group, where
  // the mix is reported as GROUP_MISMATCH.
  const numbersWithIngredients = new Set(
    rows.filter((row) => row.ingredient !== null).map(numberKey)
  )
  const groupKey = (row: ParsedManufactureRow): string => {
    if (row.manufactureNo === null) return rowKey(row)
    if (row.ingredient !== null || numbersWithIngredients.has(numberKey(row))) return numberKey(row)
    return rowKey(row)
  }

  // Insertion-ordered: drafts come out in file order of each group's first row.
  const groups = new Map<string, ParsedManufactureRow[]>()
  for (const row of rows) {
    const key = groupKey(row)
    const group = groups.get(key)
    if (group) group.push(row)
    else groups.set(key, [row])
  }

  for (const [key, group] of groups) {
    const first = group[0]
    const groupErrors: ManufactureImportError[] = []
    const fail = (row: ParsedManufactureRow, code: ManufactureImportErrorCode, value = ''): void => {
      groupErrors.push({ ref: row.ref, code, value })
    }

    // 1. Resolve every reference; report every unresolved one, not just the first.
    const products = group.map((row) => {
      const resolution = resolveProductStrict(index, row.product)
      if (resolution.status === 'ambiguous') fail(row, 'AMBIGUOUS_PRODUCT', row.product)
      else if (resolution.status === 'not_found') fail(row, 'PRODUCT_NOT_FOUND', row.product)
      return resolution.status === 'found' ? resolution.product : null
    })
    const ingredientProducts = group.map((row) => {
      if (row.ingredient === null) return null
      const resolution = resolveProductStrict(index, row.ingredient)
      if (resolution.status === 'ambiguous') fail(row, 'AMBIGUOUS_INGREDIENT', row.ingredient)
      else if (resolution.status === 'not_found') fail(row, 'INGREDIENT_NOT_FOUND', row.ingredient)
      return resolution.status === 'found' ? resolution.product : null
    })
    if (groupErrors.length > 0) {
      errors.push(...groupErrors)
      continue
    }

    // 2. All rows of the group must describe the same manufacture.
    const disagreeing: string[] = []
    if (group.some((row) => !sameDate(row.date, first.date))) disagreeing.push('date')
    if (products.some((product) => product !== products[0])) disagreeing.push('product')
    if (group.some((row) => row.quantity !== first.quantity)) disagreeing.push('quantity')
    if (group.some((row) => row.batch !== first.batch)) disagreeing.push('batch')
    if (group.some((row) => (row.ingredient === null) !== (first.ingredient === null))) {
      disagreeing.push('ingredient')
    }
    if (disagreeing.length > 0) {
      for (const row of group) fail(row, 'GROUP_MISMATCH', disagreeing.join(','))
      errors.push(...groupErrors)
      continue
    }

    // 3. The produced product must be composite; a recipe-driven draft needs a recipe whose every
    //    line has a usable quantity.
    const product = products[0]
    const overrides = first.ingredient !== null
    if (!isCompositeProduct(product)) {
      for (const row of group) fail(row, 'PRODUCT_NOT_COMPOSITE', row.product)
    } else if (!overrides && !(Array.isArray(product.ingredients) && product.ingredients.length > 0)) {
      for (const row of group) fail(row, 'EMPTY_RECIPE', row.product)
    } else if (!overrides && product.ingredients.some((line: any) => recipeLineQuantity(line?.quantity) === null)) {
      for (const row of group) fail(row, 'INVALID_RECIPE', row.product)
    }

    // 4. Explicit ingredient lines: each ingredient at most once.
    const ingredients: Array<{ product: any; perUnit: number }> = []
    if (overrides) {
      group.forEach((row, i) => {
        const ingredient = ingredientProducts[i]
        if (ingredients.some((line) => line.product === ingredient)) {
          fail(row, 'DUPLICATE_INGREDIENT', row.ingredient ?? '')
          return
        }
        ingredients.push({ product: ingredient, perUnit: row.perUnit as number })
      })
    }

    if (groupErrors.length > 0) {
      errors.push(...groupErrors)
      continue
    }

    drafts.push({
      key,
      fileName: first.ref.fileName,
      date: first.date,
      product,
      quantity: first.quantity,
      batchNumber: first.batch,
      ingredients: overrides ? ingredients : null,
      refs: group.map((row) => row.ref),
    })
  }

  return { drafts, errors }
}
